/**
 * One line of an approval card ("Bitte bestätigen"): what changes, in the customer's words.
 *
 * The backend sends every line as `{field, value}` — for a settings write that was
 * `settings.debate_mode_enabled  false`, the internal variable name and a raw JSON value
 * (bug #187). Since then a settings line also carries `setting: {name, type, value, before}`
 * (al-tutor `portal_agent_rules.describe_setting_fields`), and the card says it the way the
 * AI-configuration page does: the label from `enums.aiConfiguration.<name>`, options from
 * `enums.<name>.<value>`, booleans as an/aus, and the old value before the arrow.
 */

export interface AgentApprovalSetting {
  /** template setting name — the i18n key of its label */
  name?: string | null;
  /** template field_type: boolean, dropdown, multi-select, text, longtext, int, slider, … */
  type?: string | null;
  value?: unknown;
  before?: unknown;
}

export interface AgentApprovalField {
  field: string;
  value: string;
  setting?: AgentApprovalSetting | null;
}

/** The subset of ngx-translate's service these helpers need (keeps them testable without Angular). */
export interface Translator {
  instant(key: string): string;
}

const SETTINGS_PREFIX = 'settings.';
const OPTION_TYPES = new Set(['dropdown', 'select', 'multi-select', 'multiselect']);

function translated(t: Translator, key: string): string | null {
  const text = t.instant(key);
  return text && text !== key ? text : null;
}

function isBooleanLike(value: unknown): boolean {
  return typeof value === 'boolean' || value === 'true' || value === 'false';
}

function formatValue(t: Translator, value: unknown, setting: AgentApprovalSetting): string {
  if (value === null || value === undefined || value === '') {
    return t.instant('agentPanel.approval.empty');
  }
  const type = (setting.type || '').toLowerCase();
  if (type === 'boolean' || (!type && isBooleanLike(value))) {
    const on = value === true || value === 'true';
    return t.instant(on ? 'agentPanel.approval.on' : 'agentPanel.approval.off');
  }
  const option = (v: unknown): string =>
    (setting.name && OPTION_TYPES.has(type) && translated(t, `enums.${setting.name}.${v}`)) || String(v);
  if (Array.isArray(value)) {
    return value.length ? value.map(option).join(', ') : t.instant('agentPanel.approval.empty');
  }
  if (typeof value === 'object') {
    return t.instant('agentPanel.approval.changed');
  }
  const text = option(value);
  return text.length > 200 ? `${text.slice(0, 199)}…` : text;
}

/** Label of one line: the setting's own label, a neutral word for an unknown setting, else the field. */
export function approvalFieldLabel(t: Translator, f: AgentApprovalField): string {
  const name = f.setting?.name;
  if (name) return translated(t, `enums.aiConfiguration.${name}`) || name;
  if (f.field === 'settings' || f.field.startsWith(SETTINGS_PREFIX)) return t.instant('agentPanel.approval.setting');
  return f.field;
}

/** Value of one line: "an → aus" for a known setting, the raw value otherwise. */
export function approvalFieldValue(t: Translator, f: AgentApprovalField): string {
  const setting = f.setting;
  if (!setting) {
    // a settings line from before #187 (or an unknown key): at least no raw true/false
    if ((f.field === 'settings' || f.field.startsWith(SETTINGS_PREFIX)) && isBooleanLike(f.value)) {
      return formatValue(t, f.value, {});
    }
    return f.value;
  }
  const next = 'value' in setting ? setting.value : f.value;
  const after = formatValue(t, next, setting);
  if (!('before' in setting)) return after;
  const before = formatValue(t, setting.before, setting);
  return before === after ? after : `${before} → ${after}`;
}

/** Whether every line could be said in the customer's words — then the raw "Bisher" JSON is not needed. */
export function approvalFieldsDescribed(fields: AgentApprovalField[] | null | undefined): boolean {
  return !!fields?.length && fields.every((f) => !!f.setting?.name);
}
